import { Stack, useMediaQuery, useTheme } from "@mui/material"
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react"
import NumberFlow from "@number-flow/react"
import { colors } from "../../services/config/colors"
import Typography from "./Typography"
import { useTranslation } from "react-i18next"

interface ProgressBarProps {
  current: number
  target: number
  hidePercentage?: boolean
  containerStyle?: CSSProperties
  barStyle?: CSSProperties
}

const ProgressBar = ({
  current,
  target,
  hidePercentage,
  containerStyle,
  barStyle,
}: ProgressBarProps) => {
  const { i18n } = useTranslation()
  const theme = useTheme()
  const matchesMD = useMediaQuery(theme.breakpoints.down("md"))

  // text font size
  const fontSize = useMemo(() => (matchesMD ? 12 : 14), [matchesMD])

  // completion percentage
  const percentage = useMemo(() => (current / target) * 100, [current, target])

  // check if bar is colliding with text and change it's color accordingly
  const barRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const [barAndTextColliding, setBarAndTextColliding] = useState<boolean>(false)
  const firstCollisionCheckDone = useRef<boolean>(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: the code needs to run every time current values change
  useEffect(() => {
    const checkBarAndTextCollision = () => {
      if (
        barRef.current &&
        textRef.current &&
        barRef.current.getBoundingClientRect().right >=
          textRef.current.getBoundingClientRect().left +
            textRef.current.getBoundingClientRect().width * 0.75
      ) {
        setBarAndTextColliding(true)
      } else {
        setBarAndTextColliding(false)
      }
    }

    // check collision first time
    if (!firstCollisionCheckDone.current) {
      firstCollisionCheckDone.current = true
      checkBarAndTextCollision()
    }

    // wait for the bar transition to complete
    const timeoutId = setTimeout(checkBarAndTextCollision, 600)

    return () => clearTimeout(timeoutId)
  }, [current, target])

  return (
    <Stack
      direction="row"
      alignItems="center"
      style={{
        width: "100%",
        height: 22,
        minHeight: 22,
        borderRadius: 16,
        backgroundColor: colors.strokeLight,
        position: "relative",
        overflow: "hidden",
        ...containerStyle,
      }}
    >
      <div
        ref={barRef}
        style={{
          width: `calc(20px + ${percentage > 100 ? 100 : percentage}%)`,
          height: "100%",
          borderRadius: 16,
          backgroundColor: colors.primary,
          position: "absolute",
          left: -20,
          transition: "600ms",
          ...barStyle,
        }}
      />
      {!hidePercentage && (
        <Typography
          ref={textRef}
          fontSize={fontSize}
          fontWeight={700}
          color={barAndTextColliding ? colors.textWhite : colors.text}
          style={{
            position: "absolute",
            right: 8,
            transition: "150ms",
          }}
        >
          <NumberFlow
            value={Math.floor(percentage)}
            suffix="%"
            locales={i18n.language}
          />
        </Typography>
      )}
    </Stack>
  )
}

export default ProgressBar
